import { Routes } from '@angular/router';
import { ClientLayout } from './layouts/client/client-layout';
import { PublicLayout } from './layouts/public/public-layout';
import { AdminLayout } from './layouts/admin/admin-layout';
import { authGuard } from './core/guards/auth-guard';
import { roleGuard } from './core/guards/role-guard';
import { langGuard } from './core/guards/lang.guard';
import { HomePage } from './features/landing-page/home/home.page';
import { AdPage } from './features/landing-page/ad/ad.page';
import { LanguageRedirectComponent } from './core/components/language-redirect/language-redirect';
import { canLeaveProjectGuard } from './core/guards/can-leave-project.guard';
import { NotFoundPage } from './features/common/not-found/not-found.page';
import { TermsPage } from './features/public/legal/terms/terms.page';
import { PrivacyPage } from './features/public/legal/privacy/privacy.page';
import { ReturnsPage } from './features/public/legal/returns/returns.page';
import { CookiesPage } from './features/public/legal/cookies/cookies.page';
import { ContactPage } from './features/public/contact/contact.page';
import { inject } from '@angular/core';
import { DictionariesService } from './shared/services/data/dictionaries.service';

export const routes: Routes = [
  {
    path: 'oauth-callback',
    loadComponent: () =>
      import('./features/auth/oauth-callback/oauth-callback').then(m => m.OAuthCallbackComponent),
  },
  {
    path: '',
    component: LanguageRedirectComponent,
    pathMatch: 'full',
  },
  {
    path: ':lang',
    canActivate: [langGuard],
    runGuardsAndResolvers: 'always', // Run guard on every navigation, including param changes
    resolve: {
      dictionaries: () => inject(DictionariesService).loadDictionaries()
    },
    children: [
      {
        path: '',
        component: PublicLayout,
        children: [
          { path: '', component: HomePage, title: 'app.page_titles.home' },
          { path: 'ad', component: AdPage, title: 'app.page_titles.home' },
          {
            path: 'project',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/client/projects/create-project/create-project.page').then(m => m.CreateProjectPage),
                canDeactivate: [canLeaveProjectGuard],
                title: 'app.page_titles.creator'
              },
              {
                path: ':projectId',
                loadComponent: () =>
                  import('./features/client/projects/create-project/create-project.page').then(m => m.CreateProjectPage),
                canDeactivate: [canLeaveProjectGuard],
                title: 'app.page_titles.creator'
              }
            ]
          },
          {
            path: 'products',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/public/products/products.page').then(m => m.ProductsPage),
                title: 'app.page_titles.products'
              },
              {
                path: ':categorySlug',
                loadComponent: () =>
                  import('./features/public/products/products.page').then(m => m.ProductsPage),
                title: 'app.page_titles.products'
              },
            ]
          },
          {
            path: 'blog',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/public/blog-list/blog-list.component').then(m => m.BlogListComponent),
                title: 'app.page_titles.blog'
              },
              {
                path: ':slug',
                loadComponent: () =>
                  import('./features/public/blog-detail/blog-detail.component').then(m => m.BlogDetailComponent),
                title: 'app.page_titles.blog_post'
              }
            ]
          },
          {
            path: 'reviews',
            loadComponent: () =>
              import('./features/public/reviews/reviews-list').then(m => m.ReviewsListComponent),
            title: 'app.page_titles.reviews'
          },
          {
            path: 'checkout',
            loadComponent: () =>
              import('./features/client/checkout/checkout').then(m => m.Checkout),
            title: 'app.page_titles.checkout'
          },
          {
            path: 'checkout/success',
            loadComponent: () =>
              import('./features/client/checkout/checkout-success/checkout-success').then(m => m.CheckoutSuccessComponent),
            title: 'app.page_titles.order_confirmation'
          },
          {
            path: 'checkout/cancel',
            loadComponent: () =>
              import('./features/client/checkout/checkout').then(m => m.Checkout),
            title: 'app.page_titles.checkout'
          },
          // Policy pages
          { path: 'terms', component: TermsPage, title: 'app.page_titles.terms' },
          { path: 'privacy', component: PrivacyPage, title: 'app.page_titles.privacy' },
          { path: 'returns', component: ReturnsPage, title: 'app.page_titles.returns' },
          { path: 'cookies', component: CookiesPage, title: 'app.page_titles.cookies' },
          { path: 'contact', component: ContactPage, title: 'app.page_titles.contact' },
          // Client routes - nested inside PublicLayout
          {
            path: 'client',
            component: ClientLayout,
            canActivate: [authGuard],
            loadChildren: () =>
              import('./features/client/clients.routes').then(m => m.clientsRoutes),
          },
        ]
      },
      {
        path: 'admin',
        component: AdminLayout,
        canActivate: [authGuard, roleGuard],
        data: { roles: ['admin'] },
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./features/admin/admin.routes').then(m => m.adminRoutes),
          },
        ],
      },
      {
        path: 'error',
        component: PublicLayout,
        children: [
          { path: 'not-found', component: NotFoundPage },
          { path: '**', component: NotFoundPage }
        ]
      },
    ],
  },

];
